import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Users, Shield, Eye, X, ChevronRight, Check, Globe, Layers } from 'lucide-react';
import Swal from 'sweetalert2';
import { getAllUsersRequest, assignRolesRequest } from '../../../redux/actions/userActions';
import { getAllRolesRequest } from '../../../redux/actions/roleActions';
import { getAllModulesRequest } from '../../../redux/actions/moduleActions';
import './UserRoleAssignment.css';

/* ─── Portal ─────────────────────────────────────────────── */
const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

/* ─── Avatar ─────────────────────────────────────────────── */
const COLORS = ['#185FA5', '#D85A30', '#0F6E56', '#7c3aed', '#d97706', '#993556'];
const Avatar = ({ firstName, lastName, size = 34 }) => {
    const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
    const color = COLORS[(initials.charCodeAt(0) || 0) % COLORS.length];
    return (
        <div
            className="ura-avatar"
            style={{ background: color, width: size, height: size, minWidth: size, fontSize: size * 0.35 }}
        >
            {initials || '?'}
        </div>
    );
};

/* ─── Panel — Voir les rôles ─────────────────────────────── */
const ViewRolesPanel = ({ user, onClose }) => {
    const { t } = useTranslation();

    const grouped = useMemo(() => {
        if (!user) return {};
        return (user.roles || []).reduce((acc, r) => {
            const key = r.moduleName || 'Global';
            if (!acc[key]) acc[key] = [];
            acc[key].push(r);
            return acc;
        }, {});
    }, [user]);

    if (!user) return null;
    const totalRoles = (user.roles || []).length;

    return (
        <Portal>
            <div className="ura-overlay" onClick={onClose} />
            <div className="ura-panel">
                <div className="ura-panel-header">
                    <div className="ura-panel-user">
                        <Avatar firstName={user.firstName} lastName={user.lastName} size={38} />
                        <div>
                            <div className="ura-panel-name">{user.firstName} {user.lastName}</div>
                            <div className="ura-panel-email">{user.email}</div>
                        </div>
                    </div>
                    <button className="ura-close-btn" onClick={onClose} type="button">
                        <X size={14} />
                    </button>
                </div>

                <div className="ura-panel-body">
                    <div className="ura-panel-stat">
                        <Shield size={12} />
                        <span>{totalRoles} rôle{totalRoles !== 1 ? 's' : ''} affecté{totalRoles !== 1 ? 's' : ''}</span>
                    </div>

                    {totalRoles === 0 ? (
                        <div className="ura-empty">
                            <Shield size={30} strokeWidth={1} />
                            <p>{t('Aucun rôle affecté à cet utilisateur')}</p>
                        </div>
                    ) : (
                        <div className="ura-panel-groups">
                            {Object.entries(grouped).map(([mod, modRoles]) => (
                                <div key={mod} className="ura-panel-group">
                                    <div className="ura-group-title">
                                        {mod === 'Global' ? <Globe size={10} /> : <Layers size={10} />}
                                        {mod}
                                    </div>
                                    {modRoles.map(r => (
                                        <div key={r.id} className="ura-role-row">
                                            <span className="ura-code">{r.code}</span>
                                            <span className="ura-role-row-name">{r.name}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    );
};

/* ─── Modal — Affecter des rôles ─────────────────────────── */
const AssignModal = ({ user, modules, roles, loading, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [selectedModule, setSelectedModule] = useState(null);
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);

    useEffect(() => {
        if (user) {
            setStep(1);
            setSelectedModule(null);
            setSelectedRoleIds((user.roles || []).map(r => r.id));
        }
    }, [user]);

    const rolesForModule = useMemo(() => {
        if (selectedModule === 'global') return (roles || []).filter(r => r.isGlobal || (!r.module && !r.moduleId && !r.moduleName));
        return (roles || []).filter(r => {
            const rModId = r.module ? r.module.id : r.moduleId;
            if (rModId && String(rModId) === String(selectedModule?.id)) return true;
            if (r.moduleName && selectedModule?.name && r.moduleName === selectedModule.name) return true;
            return false;
        });
    }, [selectedModule, roles]);

    const toggleRole = (id) => {
        setSelectedRoleIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = () => {
        onSubmit(user.id, selectedRoleIds);
        onClose();
        Swal.fire({
            icon: 'success',
            title: 'Affectation réussie !',
            html: `<span style="font-size:13px;color:#374151">Rôles mis à jour pour <strong>${user.firstName} ${user.lastName}</strong></span>`,
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
        });
    };

    if (!user) return null;

    const globalCount = (roles || []).filter(r => r.isGlobal || (!r.module && !r.moduleId && !r.moduleName)).length;

    return (
        <Portal>
            <div className="ura-modal-overlay" onClick={onClose}>
                <div className="ura-modal" onClick={e => e.stopPropagation()}>

                    {/* Header */}
                    <div className="ura-modal-header">
                        <div className="ura-modal-header-left">
                            <Avatar firstName={user.firstName} lastName={user.lastName} size={38} />
                            <div>
                                <div className="ura-modal-title">{t('Affecter des rôles')}</div>
                                <div className="ura-modal-sub">{user.firstName} {user.lastName}</div>
                            </div>
                        </div>
                        <button className="ura-close-btn" onClick={onClose} type="button">
                            <X size={14} />
                        </button>
                    </div>

                    {/* Steps */}
                    <div className="ura-steps">
                        <div className={`ura-step${step >= 1 ? ' active' : ''}`}>
                            <span className="ura-step-dot">1</span>
                            <span>{t('Module')}</span>
                        </div>
                        <div className="ura-step-line" />
                        <div className={`ura-step${step >= 2 ? ' active' : ''}`}>
                            <span className="ura-step-dot">2</span>
                            <span>{t('Rôles')}</span>
                        </div>
                    </div>

                    {/* Étape 1 — Module */}
                    {step === 1 && (
                        <div className="ura-modal-body">
                            <p className="ura-hint">{t('Sélectionnez un module pour voir ses rôles disponibles')}</p>
                            <div className="ura-module-list">

                                {/* Rôles globaux */}
                                <button
                                    type="button"
                                    className="ura-module-item"
                                    onClick={() => { setSelectedModule('global'); setStep(2); }}
                                >
                                    <div className="ura-module-icon global"><Globe size={15} /></div>
                                    <div className="ura-module-info">
                                        <span className="ura-module-name">{t('Rôles Globaux')}</span>
                                        <span className="ura-module-meta">{globalCount} rôle{globalCount !== 1 ? 's' : ''}</span>
                                    </div>
                                    <ChevronRight size={14} className="ura-module-arrow" />
                                </button>

                                {(modules || []).map(m => {
                                    const count = (roles || []).filter(r => {
                                        const rModId = r.module ? r.module.id : r.moduleId;
                                        if (rModId && String(rModId) === String(m.id)) return true;
                                        if (r.moduleName && r.moduleName === m.name) return true;
                                        return false;
                                    }).length;
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            className="ura-module-item"
                                            onClick={() => { setSelectedModule(m); setStep(2); }}
                                        >
                                            <div className="ura-module-icon module"><Layers size={15} /></div>
                                            <div className="ura-module-info">
                                                <span className="ura-module-name">{m.name}</span>
                                                <span className="ura-module-meta">
                                                    {count} rôle{count !== 1 ? 's' : ''} · <code className="ura-mcode">{m.code}</code>
                                                </span>
                                            </div>
                                            <ChevronRight size={14} className="ura-module-arrow" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Étape 2 — Rôles */}
                    {step === 2 && (
                        <div className="ura-modal-body">
                            <button className="ura-back-btn" type="button" onClick={() => setStep(1)}>
                                ← {t('Changer de module')}
                            </button>
                            <div className="ura-ctx-badge">
                                {selectedModule === 'global'
                                    ? <><Globe size={11} /> {t('Global')}</>
                                    : <><Layers size={11} /> {selectedModule?.name}</>
                                }
                            </div>

                            {rolesForModule.length === 0 ? (
                                <div className="ura-empty">
                                    <Shield size={28} strokeWidth={1} />
                                    <p>{t('Aucun rôle disponible dans ce module')}</p>
                                </div>
                            ) : (
                                <div className="ura-roles-list">
                                    {rolesForModule.map(r => {
                                        const checked = selectedRoleIds.includes(r.id);
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                className={`ura-role-item${checked ? ' checked' : ''}`}
                                                onClick={() => toggleRole(r.id)}
                                            >
                                                <div className={`ura-checkbox${checked ? ' on' : ''}`}>
                                                    {checked && <Check size={10} strokeWidth={3} />}
                                                </div>
                                                <div className="ura-role-item-body">
                                                    <div className="ura-role-item-top">
                                                        <span className="ura-code">{r.code}</span>
                                                        <span className="ura-role-item-name">{r.name}</span>
                                                    </div>
                                                    {r.description && (
                                                        <div className="ura-role-item-desc">{r.description}</div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="ura-modal-footer">
                                <span className="ura-sel-count">
                                    {selectedRoleIds.length} rôle{selectedRoleIds.length !== 1 ? 's' : ''} sélectionné{selectedRoleIds.length !== 1 ? 's' : ''}
                                </span>
                                <div className="ura-footer-btns">
                                    <button type="button" className="ura-btn-outline" onClick={onClose} disabled={loading}>
                                        {t('Annuler')}
                                    </button>
                                    <button type="button" className="ura-btn-primary" onClick={handleSubmit} disabled={loading}>
                                        {loading ? t('Enregistrement...') : t('Enregistrer')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    );
};

/* ─── Composant principal ────────────────────────────────── */
const UserRoleAssignment = ({
                                users, roles, modules,
                                loadingUsers,
                                getAllUsers, getAllRoles, getAllModules, assignRoles
                            }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [viewUser, setViewUser] = useState(null);
    const [assignUser, setAssignUser] = useState(null);

    useEffect(() => {
        if (!users || users.length === 0) getAllUsers();
        if (!roles || roles.length === 0) getAllRoles();
        if (!modules || modules.length === 0) getAllModules();
    }, []);

    const filteredUsers = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();
        if (!q) return users || [];
        return (users || []).filter(u =>
            (u.firstName || '').toLowerCase().includes(q) ||
            (u.lastName || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        );
    }, [users, searchTerm]);

    return (
        <div className="ura-wrap">
            {/* En-tête */}
            <div className="ura-header">
                <h2 className="ura-title">
                    <Users size={18} />
                    {t('Affectation des Rôles')}
                </h2>
                <p className="ura-subtitle">{t('Gérez les rôles attribués à chaque utilisateur')}</p>
            </div>

            {/* Barre de recherche */}
            <div className="ura-toolbar">
                <input
                    className="ura-search"
                    placeholder={t('Rechercher un utilisateur...')}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
                <span className="ura-count">
                    {loadingUsers
                        ? t('Chargement...')
                        : `${filteredUsers.length} utilisateur${filteredUsers.length !== 1 ? 's' : ''}`
                    }
                </span>
            </div>

            {/* Tableau */}
            <div className="ura-table-wrapper">
                {loadingUsers ? (
                    <div className="ura-loading">
                        <div className="ura-spinner" />
                        <span>{t('Chargement...')}</span>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="ura-empty">
                        <p>{t('Aucun utilisateur trouvé')}</p>
                    </div>
                ) : (
                    <table className="ura-table">
                        <thead>
                        <tr>
                            <th>{t('Utilisateur')}</th>
                            <th>{t('Email')}</th>
                            <th>{t('Rôles affectés')}</th>
                            <th className="ura-th-actions">{t('Actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredUsers.map(u => {
                            const roleCount = (u.roles || []).length;
                            return (
                                <tr key={u.id}>
                                    <td>
                                        <div className="ura-user-cell">
                                            <Avatar firstName={u.firstName} lastName={u.lastName} size={34} />
                                            <div>
                                                <div className="ura-name">{u.firstName} {u.lastName}</div>
                                                {u.username && <div className="ura-username">@{u.username}</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="ura-email">{u.email}</td>
                                    <td>
                                        {roleCount === 0
                                            ? <span className="ura-badge-none">{t('Aucun rôle')}</span>
                                            : <span className="ura-badge-count">
                                                    <Shield size={11} />
                                                {roleCount} rôle{roleCount !== 1 ? 's' : ''}
                                                  </span>
                                        }
                                    </td>
                                    <td>
                                        <div className="ura-row-actions">
                                            <button
                                                type="button"
                                                className="ura-icon-btn view"
                                                title={t('Voir les rôles')}
                                                onClick={() => setViewUser(u)}
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className="ura-icon-btn assign"
                                                title={t('Affecter des rôles')}
                                                onClick={() => setAssignUser(u)}
                                            >
                                                <Shield size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Panel voir rôles */}
            <ViewRolesPanel
                user={viewUser}
                onClose={() => setViewUser(null)}
            />

            {/* Modal affecter rôles */}
            <AssignModal
                user={assignUser}
                modules={modules}
                roles={roles}
                loading={loadingUsers}
                onClose={() => setAssignUser(null)}
                onSubmit={(uid, rids) => assignRoles(uid, rids)}
            />
        </div>
    );
};

const mapStateToProps = state => ({
    users: state.user.users || [],
    roles: state.role.roles || [],
    modules: state.module.modules || [],
    loadingUsers: state.user.loading,
});

const mapDispatchToProps = dispatch => ({
    getAllUsers: () => dispatch(getAllUsersRequest()),
    getAllRoles: () => dispatch(getAllRolesRequest()),
    getAllModules: () => dispatch(getAllModulesRequest()),
    assignRoles: (userId, roleIds) => dispatch(assignRolesRequest(userId, roleIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserRoleAssignment);